import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import { runGraphqlQuery, useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type { OrganizationsByIdsQuery } from '~/graphql/graphql';

const organizationsByIdsQuery = graphql(`
  query OrganizationsByIds($ids: [ID!]!) {
    organizationsByIds(ids: $ids) {
      id
      sellerContacts {
        id
        firstName
        lastName
        phoneNumber
        externalId
      }
      notificationPreference {
        id
      }
    }
  }
`);

export type OrganizationContactData =
  OrganizationsByIdsQuery['organizationsByIds'][number];

const ORGANIZATIONS_BY_IDS_QUERY_KEY = 'organizationsByIds';

const organizationsByIdsQueryKey = (organizationIds: string[]) => [
  ORGANIZATIONS_BY_IDS_QUERY_KEY,
  ...[...organizationIds].sort(),
];

export const useOrganizationsContactData = (organizationIds: string[]) =>
  useGraphqlQuery(
    organizationsByIdsQuery,
    organizationsByIdsQueryKey(organizationIds),
    { ids: organizationIds },
    { enabled: organizationIds.length > 0 }
  );

export const useFetchOrganizationsContactData = () => {
  const queryClient = useQueryClient();
  return async (organizationIds: string[]) => {
    const data = await queryClient.fetchQuery({
      queryKey: organizationsByIdsQueryKey(organizationIds),
      queryFn: ({ signal }) =>
        runGraphqlQuery(
          organizationsByIdsQuery,
          { ids: organizationIds },
          signal
        ),
    });
    return data.organizationsByIds;
  };
};

export function organizationContactDataById(
  organizations: OrganizationContactData[] | undefined
): Map<string, OrganizationContactData> {
  return new Map(
    (organizations ?? []).map((organization) => [organization.id, organization])
  );
}

type SellerContact = OrganizationContactData['sellerContacts'][number];

export function sellerContactsOf(
  contactDataById: Map<string, OrganizationContactData>,
  organizationId: string
): SellerContact[] {
  const contactData = contactDataById.get(organizationId);
  return contactData ? contactData.sellerContacts : [];
}

export function addCachedSellerContact(
  queryClient: QueryClient,
  organizationId: string,
  contact: SellerContact
): void {
  queryClient.setQueriesData<OrganizationsByIdsQuery>(
    { queryKey: [ORGANIZATIONS_BY_IDS_QUERY_KEY] },
    (data) =>
      data && {
        ...data,
        organizationsByIds: data.organizationsByIds.map((organization) =>
          organization.id !== organizationId ||
          organization.sellerContacts.some(({ id }) => id === contact.id)
            ? organization
            : {
                ...organization,
                sellerContacts: [...organization.sellerContacts, contact],
              }
        ),
      }
  );
}
